<template>
  <Slideover :visible="visible">
    <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
      <div class="flex items-start justify-between space-x-3">
        <h2 class="text-lg leading-7 font-medium">
          {{ title }}
        </h2>
        <div class="h-7 flex items-center">
          <button
            type="button"
            aria-label="Close panel"
            @click="$emit('toggle-slideover')"
            class="text-green-300 hover:text-white transition ease-in-out duration-150"
          >
            <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
          </button>
        </div>
      </div>
    </header>

    <!-- Main -->
    <div v-if="loadingDetails" class="relative px-4 py-2">
      <LoadingSpinner />
    </div>
    <div v-else>
      <div v-if="hasRestrictions" class="p-4 text-red-500 font-bold text-sm">
        Restricted Items must be set to ZERO (0) to update the order
      </div>
      <div class="p-4 space-y-2">
        <OrderEditProduct
          v-for="(product, index) in this.editable"
          :key="index"
          :product="product"
          :shopify-line-item="getShopifyLineItem(product)"
          @quantity-updated="updateQuantity($event)"
        />
        <div
          v-if="mustCalculateRefund"
          class="flex items-center space-x-4 mt-2"
        >
          <Button
            class="flex items-center space-x-2"
            @click-handler="loadCalculatedRefund()"
            :disabled="calculateDisabled"
          >
            <span>Calculate Value</span>
            <LoadingSpinner v-if="loadingCalculatedRefund" />
          </Button>
        </div>
        <div
          v-if="showRefundValue"
          class="px-4 py-2 mt-2 border border-gray-300 rounded"
        >
          <RefundValueSummary :calculated-refund="calculatedRefund" />
        </div>
        <div v-if="shouldCancelOrder" class="px-4 text-right text-red-500">
          You should cancel the Order instead!
        </div>
      </div>

      <HttpError :error="error" />

      <!-- Footer -->
      <div class="flex-shrink-0 px-4 py-4 space-x-4 flex justify-end">
        <span class="inline-flex rounded-md shadow-sm">
          <button
            type="button"
            @click="$emit('toggle-slideover')"
            class="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
          >
            Cancel
          </button>
        </span>
        <span class="inline-flex rounded-md shadow-sm">
          <Button
            @click-handler="update"
            :disabled="!canUpdate"
            class="flex items-center space-x-2"
          >
            <span>Update</span>
            <LoadingSpinner v-if="processing" class="text-white" />
          </Button>
        </span>
      </div>
    </div>
  </Slideover>
</template>
<script>
import { ApiFactory } from "@/api/ApiFactory.js";
import OrderEditProduct from "./OrderEditProduct";
import Button from "@/components/elements/Button.vue";
import Slideover from "@/components/layout/Slideover.vue";
import RefundValueSummary from "../../RefundValue/v2/RefundValueSummary.vue";

const OrderApi = ApiFactory.get("orders");

export default {
  name: "OrderEdit",
  components: {
    Button,
    Slideover,
    OrderEditProduct,
    RefundValueSummary
  },
  props: {
    title: {
      type: String,
      default: "Title"
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      processing: false,
      error: null,
      refundValue: null,
      updatedProducts: [],
      mustCalculateRefund: true,
      calculatedRefund: null,
      loadingCalculatedRefund: false,
      shopifyDetails: null,
      loadingDetails: false
    };
  },
  computed: {
    showRefundValue() {
      return !this.mustCalculateRefund && this.calculatedRefund !== null;
    },
    calculateDisabled() {
      return (
        this.updatedProducts.length === 0 ||
        this.loadingCalculatedRefund ||
        this.shouldCancelOrder ||
        this.hasRestrictions
      );
    },
    order() {
      return this.$store.getters["orders/get"]("order");
    },
    refundableItems() {
      return this.updatedProducts.map(product => {
        return {
          sku: product.SKU,
          itemNumber: product.ItemNumber,
          quantity: product.adjustableQuantity - product.NewQuantity,
          variant_id: product.variant_id
        };
      });
    },
    editable() {
      if (!this.order.details) {
        return [];
      }
      return this.order.details.filter(detail => {
        if (detail.Adjustment != "0") {
          return false;
        }

        if (detail.Type == null || detail.Type == "K") {
          return true;
        }

        return false;
      });
    },
    shouldCancelOrder() {
      return (
        this.editable.length > 0 &&
        this.editable.length == this.updatedProducts.length &&
        this.updatedProducts.reduce(
          (sum, product) => sum + Number(product.NewQuantity),
          0
        ) == 0
      );
    },
    hasRestrictions() {
      let restrictedItems = false;
      let found = false;
      loop1: for (let item of this.editable) {
        if (item.ApprovalStatus === "RESTRICTED" || item.restricted) {
          if (
            String(item?.Product)
              .toUpperCase()
              .includes("CANCELED")
          ) {
            continue;
          }
          found = false;
          for (let updatedItem of this.updatedProducts) {
            restrictedItems = false;
            if (item.SKU === updatedItem.SKU) {
              found = true;
              if (updatedItem.NewQuantity > 0) {
                restrictedItems = true;
                break loop1;
              }
            }
          }
          if (!found) {
            restrictedItems = true;
            break;
          }
        }
      }
      return restrictedItems;
    },
    canUpdate() {
      return (
        !this.processing &&
        !this.shouldCancelOrder &&
        !this.hasRestrictions &&
        this.showRefundValue
      );
    }
  },
  async created() {
    this.loadingDetails = true;
    await OrderApi.shopifyDetails(this.order.SourceOrderNumber)
      .then(response => {
        this.shopifyDetails = response.data.body;
      })
      .catch(e => (this.error = e))
      .finally(() => {
        this.loadingDetails = false;
      });
  },
  methods: {
    getShopifyLineItem(product) {
      return this.shopifyDetails?.order?.lineItems?.edges?.find(edge =>
        edge.node.id.endsWith(product.MarketLineID)
      )?.node;
    },
    loadCalculatedRefund() {
      this.loadingCalculatedRefund = true;
      OrderApi.getCalculatedRefund(this.order.SourceOrderNumber, {
        products: this.refundableItems
      })
        .then(response => {
          this.calculatedRefund = response.data.body;
          this.loadingCalculatedRefund = false;
          this.mustCalculateRefund = false;
        })
        .catch(e => (this.error = e));
    },
    updateQuantity(event) {
      this.mustCalculateRefund = true;
      this.calculatedRefund = null;
      // remove an old entry, if exists
      this.updatedProducts = this.updatedProducts.filter(
        product => product.ItemNumber !== event.ItemNumber
      );
      // consider for updating only if quantity has changed
      if (Number(event.adjustableQuantity) == Number(event.NewQuantity)) {
        return false;
      }
      this.updatedProducts.push(event);
    },
    update() {
      this.processing = true;
      this.error = null;

      OrderApi.editOrderProducts(this.order.SourceOrderNumber, {
        adjusted_products: this.updatedProducts,
        calculatedRefund: this.calculatedRefund
      })
        .then(() => {
          this.$emit("toggle-slideover");
          this.$emit("refresh-order");
        })
        .catch(e => {
          this.error = e;
        })
        .finally(() => {
          this.processing = false;
        });
    }
  }
};
</script>
